import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ClaimTabProps } from '../claim-registration';
import { Form, Input, InputNumber, Tabs, message } from 'antd';
import { CreateMasterReservePayloadDto } from 'libs/pinnacle-sdk';
import {
  fields,
  getValidationSchema,
  initialValues,
  roundMasterReserveValues,
} from './form';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import TextArea from 'antd/es/input/TextArea';
import FormActionButtons from 'components/ui/form-action-buttons';
import { handleError, validateForm } from 'utils/form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { claimMasterReserveApi } from 'services/apis';
import { every, isEqual, isUndefined, uniq } from 'lodash';
import { EQueryKeys } from 'constants/query-keys';
import MasterReserveHistory from './components/master-reserve-history-table';
import { MetricCard } from '../schedule-of-values/metric-card';
import { formatLocaleNumber } from 'utils/format';
import BigNumber from 'bignumber.js';
import { zero } from 'constants/zero';
import {
  calculateTotalFguMarketLoss,
  calculateTotalFguMarketLossIndemnityExcCosts,
  calculateTotalIncurredCosts,
  ensureNumericValues,
} from './utils';
import { DEFAULT_CONVERSION_CURRENCY } from '../submission-details/types';
import { cn } from 'utils';
import { ECurrencies } from 'constants/enums';

const MasterReserves: React.FC<ClaimTabProps> = ({
  claim,
  includeNext,
  navigateNext,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currencyTab, setCurrencyTab] = useState<string>(
    claim.currency || DEFAULT_CONVERSION_CURRENCY
  );

  const [form] = Form.useForm<CreateMasterReservePayloadDto>();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isEditableMode, setIsEditableMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const originalValuesRef = useRef<Partial<CreateMasterReservePayloadDto>>({});

  const { data: masterReserve } = useQuery(
    [
      EQueryKeys.LIST_MASTER_RESERVE_BY_CLAIM_ID,
      claim.claimId,
      currencyTab,
      1,
      1,
    ],
    async () => {
      const masterReserveList =
        await claimMasterReserveApi.getMasterReserveListByClaimId({
          claimId: claim.claimId,
          currency: currencyTab,
          page: 1,
          pageSize: 1,
        });

      return masterReserveList?.items?.[0];
    },
    {
      enabled: !!claim.claimId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 1000 * 60 * 5,
      keepPreviousData: true,
    }
  );

  const isCurrencyMismatch = useMemo(() => {
    if (claim.currency === ECurrencies.USD) return false;

    return currencyTab !== claim.currency;
  }, [currencyTab, claim.currency]);

  const displayMasterReserve = useMemo(() => {
    if (masterReserve && isCurrencyMismatch) {
      return roundMasterReserveValues(masterReserve);
    }
    return masterReserve;
  }, [masterReserve, isCurrencyMismatch]);

  const createMasterReserveMutation = useMutation(
    (payload: CreateMasterReservePayloadDto) =>
      claimMasterReserveApi.createMasterReserve({
        claimId: claim.claimId,
        createMasterReservePayloadDto: payload,
      }),
    {
      onSuccess: (data) => {
        message.success(
          t('forms.updateRecordSuccess', {
            type: t('claim.navigation.masterReserves'),
          })
        );
        queryClient.invalidateQueries({
          queryKey: [EQueryKeys.LIST_MASTER_RESERVE_BY_CLAIM_ID],
          exact: false,
        });
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('forms.updateRecordFail', {
            type: t('claim.navigation.masterReserves'),
          }),
        });
      },
    }
  );

  const validateAndSetFormValidationState = useCallback(async () => {
    const currentValues = form.getFieldsValue(fields);
    const isValid = await validateForm<CreateMasterReservePayloadDto>(
      currentValues,
      form,
      fields,
      getValidationSchema(t)
    );
    setIsFormValid(isValid);
    const normalizedCurrentValues = ensureNumericValues(currentValues);
    const isDirtyNow = !isEqual(
      normalizedCurrentValues,
      originalValuesRef.current
    );
    setIsDirty(isDirtyNow);
  }, [form, t]);

  useEffect(() => {
    if (!displayMasterReserve) {
      form.setFieldsValue(initialValues);
      setIsEditableMode(true);
    } else {
      form.setFieldsValue(displayMasterReserve);
      setIsEditableMode(false);
    }
    originalValuesRef.current = ensureNumericValues(
      masterReserve ? masterReserve : initialValues
    );
    setIsDirty(false);
    validateAndSetFormValidationState();
  }, [displayMasterReserve, form, validateAndSetFormValidationState]);

  const [
    watchedLossAdjustorCosts,
    watchedLegalExpertsCosts,
    watchedConsultantsCosts,
    watchedOtherFees,
    watchedIndemnityMaterialDamage,
    watchedIndemnityBusinessInterruption,
    watchedUnderliningSirGovtProgramsSir,
  ] = [
    Form.useWatch('lossAdjustorCosts', form),
    Form.useWatch('legalExpertsCosts', form),
    Form.useWatch('consultantsCosts', form),
    Form.useWatch('otherFees', form),
    Form.useWatch('indemnityMaterialDamage', form),
    Form.useWatch('indemnityBusinessInterruption', form),
    Form.useWatch('underliningSirGovtProgramsSir', form),
  ];

  const totalIncurredCosts = useMemo(
    () =>
      calculateTotalIncurredCosts({
        lossAdjustorCosts: new BigNumber(watchedLossAdjustorCosts || zero),
        legalExpertsCosts: new BigNumber(watchedLegalExpertsCosts || zero),
        consultantsCosts: new BigNumber(watchedConsultantsCosts || zero),
        otherFees: new BigNumber(watchedOtherFees || zero),
      }),
    [
      watchedLossAdjustorCosts,
      watchedLegalExpertsCosts,
      watchedConsultantsCosts,
      watchedOtherFees,
    ]
  );

  const totalFguMarketLossIndemnityExcCosts = useMemo(
    () =>
      calculateTotalFguMarketLossIndemnityExcCosts({
        indemnityMaterialDamage: new BigNumber(
          watchedIndemnityMaterialDamage || zero
        ),
        indemnityBusinessInterruption: new BigNumber(
          watchedIndemnityBusinessInterruption || zero
        ),
        underliningSirGovtProgramsSir: new BigNumber(
          watchedUnderliningSirGovtProgramsSir || zero
        ),
      }),
    [
      watchedIndemnityMaterialDamage,
      watchedIndemnityBusinessInterruption,
      watchedUnderliningSirGovtProgramsSir,
    ]
  );

  const totalFguMarketLoss = useMemo(
    () =>
      calculateTotalFguMarketLoss({
        totalIncurredCosts,
        totalFguMarketLossIndemnityExcCosts,
      }),
    [totalIncurredCosts, totalFguMarketLossIndemnityExcCosts]
  );

  const handleOnFinish = useCallback(async () => {
    if (!isDirty) {
      return;
    }
    const fieldsValue = form.getFieldsValue();
    const payload: CreateMasterReservePayloadDto = {
      ...fieldsValue,
    };
    createMasterReserveMutation.mutate(payload);
  }, [createMasterReserveMutation, form, isDirty]);

  return (
    <div>
      <Tabs
        activeKey={currencyTab}
        onChange={setCurrencyTab}
        items={uniq([claim.currency, DEFAULT_CONVERSION_CURRENCY]).map(
          (currency) => ({
            key: currency,
            label: `${currency} ${t('general.currency')}`,
          })
        )}
        className={'custom-tabs-with-bottom-curve'}
        tabPosition="right"
      />

      <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:gap-6">
        <MetricCard
          title={t('claim.masterReserves.metrics.totalFguMarketLossIncCosts')}
          value={
            isEditableMode
              ? formatLocaleNumber(totalFguMarketLoss.toNumber())
              : formatLocaleNumber(
                  Number(displayMasterReserve?.totalFguMarketLoss || 0)
                )
          }
          disableAbbreviation
        />
        <MetricCard
          title={t(
            'claim.masterReserves.metrics.fguMarketLossIndemnityExcCosts'
          )}
          value={
            isEditableMode
              ? formatLocaleNumber(
                  totalFguMarketLossIndemnityExcCosts.toNumber()
                )
              : formatLocaleNumber(
                  Number(
                    displayMasterReserve?.totalFguMarketLossIndemnityExcCosts ||
                      0
                  )
                )
          }
          disableAbbreviation
        />
        <MetricCard
          title={t('claim.masterReserves.metrics.totalIncurredCosts')}
          value={
            isEditableMode
              ? formatLocaleNumber(totalIncurredCosts.toNumber())
              : formatLocaleNumber(
                  Number(displayMasterReserve?.totalIncurredCosts || 0)
                )
          }
          disableAbbreviation
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={validateAndSetFormValidationState}
        disabled={!isEditableMode}
      >
        <FormSection
          title={t(
            'claim.masterReserves.sections.indemnityIncurredReserveBreakdown'
          )}
          collapsible
        >
          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="indemnityMaterialDamage"
              label={t(
                'claim.masterReserves.indemnity.indemnityMaterialDamage'
              )}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="indemnityBusinessInterruption"
              label={t(
                'claim.masterReserves.indemnity.indemnityBusinessInterruption'
              )}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>

            <Form.Item
              name="underliningSirGovtProgramsSir"
              label={t(
                'claim.masterReserves.indemnity.underliningSirGovtProgramsSir'
              )}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>

            <Form.Item
              name="reservingComments"
              label={t('claim.masterReserves.indemnity.reservingComments')}
            >
              <TextArea rows={4}></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection
          title={t('claim.masterReserves.sections.costsReserveBreakdown')}
          collapsible
        >
          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="lossAdjustorCosts"
              label={t('claim.masterReserves.costs.lossAdjustorCosts')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>

            <Form.Item
              name="consultantsCosts"
              label={t('claim.masterReserves.costs.consultantsCosts')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="legalExpertsCosts"
              label={t('claim.masterReserves.costs.legalExpertsCosts')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>

            <Form.Item
              name="otherFees"
              label={t('claim.masterReserves.costs.otherFees')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="claimsFeesComments"
              label={t('claim.masterReserves.costs.claimsFeesComments')}
            >
              <TextArea rows={4}></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection
          title={t('claim.masterReserves.sections.programRiConsiderations')}
          collapsible
        >
          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="programCessionDeclarationName"
              label={t(
                'claim.masterReserves.programRi.programCessionDeclarationName'
              )}
            >
              <Input size="large"></Input>
            </Form.Item>

            <Form.Item
              name="programCessionPercent"
              label={t('claim.masterReserves.programRi.programCessionPercent')}
            >
              <InputNumber
                size="large"
                className="w-full"
                addonAfter="%"
                step="0.0001"
                precision={4}
              ></InputNumber>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="captiveSirsAag"
              label={t('claim.masterReserves.programRi.captiveSirsAag')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>

            <Form.Item
              name="riSirAag"
              label={t('claim.masterReserves.programRi.riSirAag')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="riCessionPercent"
              label={t('claim.masterReserves.programRi.riCessionPercent')}
            >
              <InputNumber
                size="large"
                className="w-full"
                addonAfter="%"
                step="0.0001"
                precision={4}
              ></InputNumber>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection
          title={t('claim.masterReserves.sections.furtherClaimsFinancials')}
          collapsible
        >
          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="projectedUltimateLoss"
              label={t('claim.masterReserves.financials.projectedUltimateLoss')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={2}>
            <Form.Item
              name="recoveryPotential"
              label={t('claim.masterReserves.financials.recoveryPotential')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>

            <Form.Item
              name="recoveryActual"
              label={t('claim.masterReserves.financials.recoveryActual')}
            >
              <InputNumber
                size="large"
                formatter={(value) => formatLocaleNumber(Number(value))}
                className="w-full"
              ></InputNumber>
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item
              name="recoveryComments"
              label={t('claim.masterReserves.financials.recoveryComments')}
            >
              <TextArea rows={4}></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>
      </Form>

      <MasterReserveHistory
        claimId={claim.claimId}
        currency={currencyTab}
        isCurrencyMismatch={isCurrencyMismatch}
      />

      {isEditableMode ? (
        <FormActionButtons
          cancelText={t('claim.masterReserves.actions.cancel')}
          onCancel={() => setIsEditableMode(false)}
          cancelColor="primary"
          confirmText={t('claim.masterReserves.actions.save')}
          confirmOutlined={false}
          onConfirm={() => {
            handleOnFinish();
            setIsEditableMode(false);
          }}
          isConfirmDisabled={
            !isFormValid || createMasterReserveMutation.isLoading || !isDirty
          }
        ></FormActionButtons>
      ) : (
        <FormActionButtons
          cancelText={t('claim.masterReserves.actions.updateReserve')}
          onCancel={() => setIsEditableMode(true)}
          cancelColor="primary"
          confirmText={t('claim.masterReserves.actions.next')}
          confirmOutlined={false}
          onConfirm={navigateNext}
          includeConfirm={includeNext}
          isCancelDisabled={isCurrencyMismatch}
        ></FormActionButtons>
      )}
    </div>
  );
};

export default MasterReserves;
